import React from "react";
import { Modal } from "antd";
import {
  Archive,
  ShieldCheck,
  PauseCircle,
  CreditCard,
  HelpCircle,
  Loader2,
} from "lucide-react";
import CouponInput from "../../components/CouponInput";

const DisconnectOrgModal = ({
  open,
  onCancel,
  onConfirm,
  loading = false,
  organizationName,
  provider = "xero",
  hasDefaultPaymentMethod = false,
  couponCode = "",
  onCouponCodeChange,
  appliedDiscount = null,
  onDiscountApplied,
}) => {
  const isQbo = provider === "qbo";
  const brand = isQbo ? "QuickBooks Online" : "Xero";

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={520}
      className="vault-downgrade-modal font-sans"
      title={
        <div className="flex items-center gap-3 pb-3 border-b border-gray-100 font-sans">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 border border-amber-200/60 shadow-sm shrink-0">
            <Archive className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0 leading-tight">
              Downgrade to Vault
            </h3>
            <p className="text-xs text-gray-500 m-0 mt-0.5">
              Switch this organization to cloud archive retention
            </p>
          </div>
        </div>
      }
    >
      <div className="pt-4 space-y-4 font-sans text-gray-700">
        {/* Organization Card */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200/80">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                isQbo
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-sky-50 text-sky-700 border border-sky-200"
              }`}
            >
              {brand}
            </span>
            <span className="text-sm font-semibold text-gray-900 truncate">
              {organizationName || "Selected Organization"}
            </span>
          </div>
          <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200/60 shrink-0">
            Vault Plan
          </span>
        </div>

        {/* What changes with Vault */}
        <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 space-y-3">
          <div className="flex items-start gap-3 text-xs leading-relaxed text-gray-600">
            <div className="mt-0.5 p-1 rounded-full bg-blue-50 text-blue-600 shrink-0">
              <ShieldCheck className="h-3.5 w-3.5" />
            </div>
            <div>
              <span className="font-semibold text-gray-900">Backups remain secure: </span>
              All existing backups stay preserved and accessible in the cloud while your Vault plan is active.
            </div>
          </div>

          <div className="flex items-start gap-3 text-xs leading-relaxed text-gray-600">
            <div className="mt-0.5 p-1 rounded-full bg-amber-50 text-amber-600 shrink-0">
              <PauseCircle className="h-3.5 w-3.5" />
            </div>
            <div>
              <span className="font-semibold text-gray-900">Live sync paused: </span>
              Daily automated backups from your {brand} company will be stopped.
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-600 pt-2.5 border-t border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="p-1 rounded-full bg-emerald-50 text-emerald-600 shrink-0">
                <CreditCard className="h-3.5 w-3.5" />
              </div>
              <span className="font-medium text-gray-900">Vault Plan:</span>
              <span className="text-gray-500">Archive retention</span>
            </div>
            <div className="text-right">
              <div>
                <span className="text-sm font-bold text-gray-900">$2.49</span>
                <span className="text-[11px] text-gray-500 font-normal"> / month</span>
              </div>
              <div className="text-[10px] text-gray-400">Base price + applicable taxes</div>
            </div>
          </div>
        </div>

        {/* QBO Notice */}
        {isQbo && (
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50/80 p-2.5 rounded-lg border border-gray-200/60">
            <HelpCircle className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span>After confirming, you may be redirected to complete payment if your account requires it.</span>
          </div>
        )}

        {/* Coupon Input */}
        {hasDefaultPaymentMethod && (
          <div className="pt-2 border-t border-gray-100">
            <CouponInput
              value={couponCode}
              onChange={onCouponCodeChange}
              appliedDiscount={appliedDiscount}
              onDiscountApplied={onDiscountApplied}
              label="Have a coupon code?"
              labelClassName="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5"
            />
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Downgrading...</span>
              </>
            ) : (
              <span>Downgrade to Vault</span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DisconnectOrgModal;
